package com.trevorwiebe.timesheet.mileage.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.UserRole
import com.trevorwiebe.timesheet.mileage.data.ExtractedReceiptData
import com.trevorwiebe.timesheet.mileage.data.ReceiptExtractionService
import com.trevorwiebe.timesheet.mileage.data.ReceiptStorageRepository
import com.trevorwiebe.timesheet.mileage.domain.LocationRepository
import com.trevorwiebe.timesheet.mileage.domain.findMatchingLocation
import com.trevorwiebe.timesheet.mileage.domain.MileageRepository
import com.trevorwiebe.timesheet.mileage.domain.EquipmentRepository
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.LocationType
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.OdometerNeighbors
import com.trevorwiebe.timesheet.mileage.domain.model.OdometerPlacement
import com.trevorwiebe.timesheet.mileage.domain.model.evaluateOdometerPlacement
import com.trevorwiebe.timesheet.mileage.domain.model.odometerConflictMessage
import com.trevorwiebe.timesheet.mileage.domain.model.odometerLookupErrorMessage
import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.mileage.domain.model.TripType
import com.trevorwiebe.timesheet.mileage.domain.model.TripTypeData
import com.trevorwiebe.timesheet.mileage.domain.usecases.AddMileageEntry
import com.trevorwiebe.timesheet.mileage.domain.usecases.DeleteMileageEntry
import com.trevorwiebe.timesheet.mileage.domain.usecases.UpdateMileageEntry
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Duration.Companion.minutes

class MileageViewModel(
    private val mileageRepository: MileageRepository,
    private val equipmentRepository: EquipmentRepository,
    private val locationRepository: LocationRepository,
    private val coreRepository: CoreRepository,
    private val addMileageEntry: AddMileageEntry,
    private val updateMileageEntry: UpdateMileageEntry,
    private val deleteMileageEntry: DeleteMileageEntry,
    private val receiptExtractionService: ReceiptExtractionService,
    private val receiptStorageRepository: ReceiptStorageRepository
) : DisposableViewModel() {

    private val _uiState = MutableStateFlow(MileageUiState())
    val uiState = _uiState.asStateFlow()

    private var entriesJob: Job? = null

    init {
        val (start, today) = Util.lastTwoMonthsRange()
        _uiState.update { it.copy(filter = it.filter.copy(startDate = start, endDate = today)) }
        loadData()
    }

    private fun loadData() {
        viewModelScope.launch {
            val userResult = coreRepository.getDatabaseUser()
            if (!userResult.error.isNullOrEmpty()) {
                _uiState.update { it.copy(isLoading = false, error = userResult.error) }
                return@launch
            }
            val currentUser = userResult.data as DatabaseUserModel
            _uiState.update { it.copy(currentUser = currentUser) }

            val isElevated = currentUser.role == UserRole.ADMIN || currentUser.role == UserRole.DISPATCHER
            if (isElevated) loadUserList()

            // currentUser is now in state, so the server-side query can resolve its employee scope.
            loadEntries()
        }

        loadEquipment()
        loadLocations()
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadEntries() {
        val state = _uiState.value
        val currentUser = state.currentUser ?: return
        val filter = state.filter
        val start = filter.startDate ?: return
        val end = filter.endDate ?: return

        val isElevated = currentUser.role == UserRole.ADMIN || currentUser.role == UserRole.DISPATCHER
        val effectiveEmployeeId = if (isElevated) filter.employeeId else currentUser.id

        entriesJob?.cancel()
        _uiState.update { it.copy(isLoading = true) }
        val job = viewModelScope.launch {
            val flow = mileageRepository.queryMileageEntries(
                startDate = start,
                endDate = end,
                employeeId = effectiveEmployeeId,
                equipmentId = filter.equipmentId,
                tripTypes = filter.tripTypes,
            )
            flow.collect { result ->
                if (result.error.isNullOrEmpty()) {
                    _uiState.update { it.copy(entries = result.data as List<MileageEntry>, isLoading = false) }
                } else {
                    _uiState.update { it.copy(isLoading = false, error = result.error) }
                }
            }
        }
        entriesJob = job
        FirestoreListenerRegistry.registerJob(job)
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadEquipment() {
        val job = viewModelScope.launch {
            val flow = equipmentRepository.getEquipment()
            flow.collect { result ->
                if (result.error.isNullOrEmpty()) {
                    _uiState.update { it.copy(equipment = result.data as List<Equipment>) }
                }
            }
        }
        FirestoreListenerRegistry.registerJob(job)
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadLocations() {
        val job = viewModelScope.launch {
            val flow = locationRepository.getLocations()
            flow.collect { result ->
                if (result.error.isNullOrEmpty()) {
                    _uiState.update { it.copy(locations = result.data as List<Location>) }
                }
            }
        }
        FirestoreListenerRegistry.registerJob(job)
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadUserList() {
        viewModelScope.launch {
            val result = coreRepository.getUserList()
            if (result.error.isNullOrEmpty()) {
                _uiState.update { it.copy(userList = result.data as List<DatabaseUserModel>) }
            }
        }
    }

    fun onEvent(event: MileageEvent) {
        when (event) {
            MileageEvent.OnShowAddDialog -> {
                val currentUser = _uiState.value.currentUser
                val isElevated = currentUser?.role == UserRole.ADMIN || currentUser?.role == UserRole.DISPATCHER
                if (isElevated && _uiState.value.userList.isEmpty()) loadUserList()
                val lastEntry = _uiState.value.entries
                    .filter { it.employeeId == currentUser?.id }
                    .maxByOrNull { it.date }
                val freshForm = AddMileageFormState(
                    selectedEmployeeId = currentUser?.id ?: "",
                    selectedEquipmentId = lastEntry?.equipmentId ?: "",
                    selectedTrailerId = lastEntry?.trailerId
                )
                _uiState.update { it.copy(showAddDialog = true, editingEntry = null, addEntryForm = freshForm, odometerError = null, isEditingLocked = false) }
            }

            MileageEvent.OnDismissAddDialog ->
                _uiState.update {
                    it.copy(
                        showAddDialog = false,
                        editingEntry = null,
                        odometerError = null,
                        pendingReceiptBytes = null,
                        extractedReceiptData = null,
                        isLoadingReceiptImage = false,
                        isEditingLocked = false,
                    )
                }

            is MileageEvent.OnUpdateForm ->
                _uiState.update { it.copy(addEntryForm = event.form) }

            is MileageEvent.OnSaveEntry -> {
                // SERVICE entries are admin-only to create; the UI hides the chip, this
                // guard backstops a stale client.
                if (event.entry.tripType == TripType.SERVICE && !_uiState.value.isAdmin) return
                saveEntry(event.entry, event.newLocation)
            }

            is MileageEvent.OnSetOdometerError ->
                _uiState.update { it.copy(odometerError = event.message) }

            MileageEvent.OnConfirmOdometerWarning -> {
                val pending = _uiState.value.pendingEntry ?: return
                val pendingLoc = _uiState.value.pendingLocation
                val pendingAutoStart = _uiState.value.pendingAutoStart
                val isEdit = _uiState.value.pendingIsEdit
                _uiState.update {
                    it.copy(
                        showOdometerWarning = false,
                        pendingEntry = null,
                        pendingLocation = null,
                        pendingAutoStart = null,
                        pendingOdometerDifference = null,
                        pendingIsEdit = false
                    )
                }
                viewModelScope.launch {
                    if (isEdit) performUpdate(pending, pendingLoc) else performSave(pending, pendingLoc, pendingAutoStart)
                }
            }

            MileageEvent.OnDismissOdometerWarning ->
                _uiState.update {
                    it.copy(
                        showOdometerWarning = false,
                        pendingEntry = null,
                        pendingLocation = null,
                        pendingAutoStart = null,
                        pendingOdometerDifference = null,
                        pendingIsEdit = false
                    )
                }

            is MileageEvent.OnShowEditDialog -> {
                val e = event.entry
                if (_uiState.value.isElevated && _uiState.value.userList.isEmpty()) loadUserList()
                val base = AddMileageFormState(
                    date = e.date,
                    selectedEmployeeId = e.employeeId,
                    selectedEquipmentId = e.equipmentId,
                    selectedTrailerId = e.trailerId,
                    mileageText = e.mileageValue.toString(),
                    selectedTripTypeOption = TripTypeOption.fromTripType(e.tripType)
                )
                val form = when (val data = e.tripTypeData) {
                    is TripTypeData.StateLine -> base.copy(fromState = data.fromState, toState = data.toState, highwayText = data.highway)
                    is TripTypeData.Fuel -> base.copy(
                        selectedLocationId = data.locationId,
                        gallonsText = if (data.gallons != 0.0) data.gallons.toString() else ""
                    )
                    is TripTypeData.Loading -> base.copy(selectedLocationId = data.locationId, loadingUnloadingNotesText = data.notes)
                    is TripTypeData.Unloading -> base.copy(selectedLocationId = data.locationId, loadingUnloadingNotesText = data.notes)
                    is TripTypeData.Service -> base.copy(serviceLocationText = data.serviceLocation)
                    is TripTypeData.Other -> base.copy(otherNotesText = data.notes, selectedLocationId = data.locationId ?: "")
                    is TripTypeData.StartEnd -> base.copy(startEndLocationText = data.location)
                }
                val locked = !_uiState.value.canModify(e)
                _uiState.update { it.copy(editingEntry = e, showAddDialog = true, addEntryForm = form, odometerError = null, pendingReceiptBytes = null, isEditingLocked = locked) }
                val receiptUrl = (e.tripTypeData as? TripTypeData.Fuel)?.receiptImageUrl
                if (receiptUrl != null) {
                    viewModelScope.launch {
                        _uiState.update { it.copy(isLoadingReceiptImage = true) }
                        val bytes = receiptStorageRepository.downloadBytes(receiptUrl)
                        _uiState.update {
                            it.copy(
                                isLoadingReceiptImage = false,
                                pendingReceiptBytes = bytes?.let { b -> PendingImageBytes(b, isNewUpload = false) }
                            )
                        }
                    }
                }
            }

            is MileageEvent.OnUpdateEntry -> {
                // Guard on the frozen flag / original entry, never on event.entry (whose
                // date a caller could have changed to dodge the one-week lock).
                val s = _uiState.value
                if (s.isEditingLocked || s.editingEntry?.let { !s.canModify(it) } == true) return
                // Non-admins may keep an existing SERVICE entry as SERVICE, but not switch one to it.
                if (event.entry.tripType == TripType.SERVICE && !s.isAdmin &&
                    s.editingEntry?.tripType != TripType.SERVICE
                ) return
                updateEntry(event.entry, event.newLocation)
            }

            is MileageEvent.OnRequestDelete -> {
                if (!_uiState.value.canModify(event.entry)) return
                _uiState.update { it.copy(entryToDelete = event.entry, showDeleteConfirm = true) }
            }

            MileageEvent.OnConfirmDelete -> {
                val entry = _uiState.value.entryToDelete ?: return
                if (!_uiState.value.canModify(entry)) return
                _uiState.update { it.copy(entryToDelete = null, showDeleteConfirm = false, showAddDialog = false, editingEntry = null, isEditingLocked = false) }
                viewModelScope.launch { deleteMileageEntry.invoke(entry.id) }
            }

            MileageEvent.OnDismissDelete ->
                _uiState.update { it.copy(entryToDelete = null, showDeleteConfirm = false) }

            is MileageEvent.OnFilterByEquipment -> {
                _uiState.update { it.copy(filter = it.filter.copy(equipmentId = event.equipmentId)) }
                loadEntries()
            }

            is MileageEvent.OnFilterByTripType -> {
                _uiState.update { it.copy(filter = it.filter.copy(tripTypes = event.types)) }
                loadEntries()
            }

            is MileageEvent.OnFilterByEmployee -> {
                _uiState.update { it.copy(filter = it.filter.copy(employeeId = event.employeeId)) }
                loadEntries()
            }

            MileageEvent.OnStartDateClick ->
                _uiState.update { it.copy(filter = it.filter.copy(showStartPicker = true)) }

            MileageEvent.OnEndDateClick ->
                _uiState.update { it.copy(filter = it.filter.copy(showEndPicker = true)) }

            is MileageEvent.OnStartDateSelected -> {
                _uiState.update { it.copy(filter = it.filter.copy(startDate = event.date, showStartPicker = false)) }
                loadEntries()
            }

            is MileageEvent.OnEndDateSelected -> {
                _uiState.update { it.copy(filter = it.filter.copy(endDate = event.date, showEndPicker = false)) }
                loadEntries()
            }

            MileageEvent.OnDismissStartPicker ->
                _uiState.update { it.copy(filter = it.filter.copy(showStartPicker = false)) }

            MileageEvent.OnDismissEndPicker ->
                _uiState.update { it.copy(filter = it.filter.copy(showEndPicker = false)) }

            MileageEvent.OnClearFilters -> {
                val (start, today) = Util.lastTwoMonthsRange()
                _uiState.update { it.copy(filter = MileageFilter(startDate = start, endDate = today)) }
                loadEntries()
            }

            is MileageEvent.OnReceiptImageSelected -> {
                if (_uiState.value.isEditingLocked) return
                _uiState.update {
                    it.copy(
                        isExtractingReceipt = true,
                        pendingReceiptBytes = PendingImageBytes(event.imageBytes),
                        addEntryForm = it.addEntryForm.copy(receiptError = null)
                    )
                }
                viewModelScope.launch {
                    val extracted = receiptExtractionService.extract(event.imageBytes)
                    _uiState.update { state ->
                        state.copy(
                            isExtractingReceipt = false,
                            extractedReceiptData = extracted,
                            addEntryForm = applyExtractedReceipt(state.addEntryForm, extracted, state.locations)
                        )
                    }
                }
            }

            MileageEvent.OnShowPhotoViewer ->
                _uiState.update { it.copy(showPhotoViewer = true) }

            MileageEvent.OnDismissPhotoViewer ->
                _uiState.update { it.copy(showPhotoViewer = false) }

            is MileageEvent.OnPhotoSaved -> {
                if (_uiState.value.isEditingLocked) return
                _uiState.update {
                    it.copy(
                        pendingReceiptBytes = PendingImageBytes(event.rotatedBytes, isNewUpload = true),
                        showPhotoViewer = false,
                        addEntryForm = it.addEntryForm.copy(receiptError = null)
                    )
                }
            }

            MileageEvent.OnPhotoDeleted -> {
                if (_uiState.value.isEditingLocked) return
                _uiState.update {
                    it.copy(
                        pendingReceiptBytes = null,
                        extractedReceiptData = null,
                        showPhotoViewer = false
                    )
                }
            }
        }
    }

    /**
     * Applies receipt-extracted fuel data to the form. The fuel location is merged
     * automatically: if an existing FUEL location matches the extracted vendor/city/state
     * it is selected; otherwise the new-location fields are pre-filled (created on save).
     */
    private fun applyExtractedReceipt(
        form: AddMileageFormState,
        extracted: ExtractedReceiptData,
        locations: List<Location>
    ): AddMileageFormState {
        val vendor = extracted.fuelVendor?.trim().orEmpty()
        val city = extracted.city?.trim().orEmpty()
        val state = extracted.state?.trim().orEmpty()

        val withGallons = if (extracted.gallons != null) {
            form.copy(gallonsText = extracted.gallons.toString())
        } else form

        if (vendor.isEmpty()) return withGallons

        val match = locations.findMatchingLocation(LocationType.FUEL, vendor, city, state)
        return if (match != null) {
            withGallons.copy(
                selectedLocationId = match.id,
                isNewLocation = false,
                locationError = null
            )
        } else {
            withGallons.copy(
                isNewLocation = true,
                selectedLocationId = "",
                newLocationName = vendor,
                newLocationCity = city,
                newLocationState = state,
                locationError = null
            )
        }
    }

    private fun saveEntry(entry: MileageEntry, newLocation: Location?) {
        viewModelScope.launch {
            _uiState.update { it.copy(isSaving = true, odometerError = null) }
            val neighborsResult = mileageRepository
                .getOdometerNeighbors(entry.equipmentId, entry.date, entry.mileageValue, null)
            val autoStart = carriedForwardStartOrNull(
                entry = entry,
                previousByDate = (neighborsResult.data as? OdometerNeighbors)?.previousByDate,
            )
            when (val check = checkOdometer(entry, neighborsResult)) {
                is OdometerCheck.Blocked ->
                    _uiState.update { it.copy(isSaving = false, odometerError = check.message) }
                is OdometerCheck.Warn -> _uiState.update {
                    it.copy(
                        isSaving = false,
                        pendingEntry = entry,
                        pendingLocation = newLocation,
                        pendingAutoStart = autoStart,
                        pendingOdometerDifference = check.difference,
                        pendingIsEdit = false,
                        showOdometerWarning = true
                    )
                }
                OdometerCheck.Ok -> performSave(entry, newLocation, autoStart)
            }
        }
    }

    private fun updateEntry(entry: MileageEntry, newLocation: Location?) {
        viewModelScope.launch {
            _uiState.update { it.copy(isSaving = true, odometerError = null) }
            val neighborsResult = mileageRepository
                .getOdometerNeighbors(entry.equipmentId, entry.date, entry.mileageValue, _uiState.value.editingEntry?.id)
            when (val check = checkOdometer(entry, neighborsResult)) {
                is OdometerCheck.Blocked ->
                    _uiState.update { it.copy(isSaving = false, odometerError = check.message) }
                is OdometerCheck.Warn -> _uiState.update {
                    it.copy(
                        isSaving = false,
                        pendingEntry = entry,
                        pendingLocation = newLocation,
                        pendingOdometerDifference = check.difference,
                        pendingIsEdit = true,
                        showOdometerWarning = true
                    )
                }
                OdometerCheck.Ok -> performUpdate(entry, newLocation)
            }
        }
    }

    private sealed interface OdometerCheck {
        data object Ok : OdometerCheck
        data class Blocked(val message: String) : OdometerCheck
        data class Warn(val difference: Long) : OdometerCheck
    }

    private fun checkOdometer(entry: MileageEntry, result: TSResult): OdometerCheck {
        if (result.error != null) {
            return OdometerCheck.Blocked(odometerLookupErrorMessage(result.error))
        }
        val neighbors = result.data as? OdometerNeighbors ?: OdometerNeighbors()
        return when (val placement = evaluateOdometerPlacement(entry.mileageValue, neighbors)) {
            is OdometerPlacement.Conflict ->
                OdometerCheck.Blocked(odometerConflictMessage(entry.mileageValue, placement))
            is OdometerPlacement.LargeJump -> OdometerCheck.Warn(placement.milesSincePrevious)
            OdometerPlacement.Fits -> OdometerCheck.Ok
        }
    }

    private suspend fun performSave(entry: MileageEntry, newLocation: Location?, autoStart: MileageEntry? = null) {
        val finalEntry = uploadReceiptIfNeeded(entry) ?: return
        autoStart?.let { mileageRepository.addMileageEntry(it) }
        val result = addMileageEntry.invoke(finalEntry, newLocation)
        if (result.error.isNullOrEmpty()) {
            _uiState.update {
                it.copy(
                    isSaving = false,
                    showAddDialog = false,
                    editingEntry = null,
                    odometerError = null,
                    pendingReceiptBytes = null,
                    extractedReceiptData = null,
                    isEditingLocked = false,
                )
            }
        } else {
            _uiState.update { it.copy(isSaving = false, error = result.error) }
        }
    }

    private suspend fun performUpdate(entry: MileageEntry, newLocation: Location?) {
        val finalEntry = uploadReceiptIfNeeded(entry) ?: return
        val result = updateMileageEntry.invoke(finalEntry, newLocation)
        if (result.error.isNullOrEmpty()) {
            _uiState.update {
                it.copy(
                    isSaving = false,
                    showAddDialog = false,
                    editingEntry = null,
                    odometerError = null,
                    pendingReceiptBytes = null,
                    extractedReceiptData = null,
                    isEditingLocked = false,
                )
            }
        } else {
            _uiState.update { it.copy(isSaving = false, error = result.error) }
        }
    }

    private suspend fun uploadReceiptIfNeeded(entry: MileageEntry): MileageEntry? {
        val pending = _uiState.value.pendingReceiptBytes
        val existingUrl = (_uiState.value.editingEntry?.tripTypeData as? TripTypeData.Fuel)?.receiptImageUrl
        val isEdit = _uiState.value.editingEntry != null

        if (pending == null || !pending.isNewUpload) {
            // No new image — preserve any existing receipt URL
            if (existingUrl != null && entry.tripTypeData is TripTypeData.Fuel) {
                return entry.copy(
                    tripTypeData = (entry.tripTypeData as TripTypeData.Fuel).copy(receiptImageUrl = existingUrl)
                )
            }
            return entry
        }
        if (entry.tripTypeData !is TripTypeData.Fuel) return entry
        val orgId = _uiState.value.currentUser?.organizationId ?: return entry
        val userId = _uiState.value.currentUser?.id ?: return entry

        // For edits, use the entry ID as the filename so the file is overwritten in place.
        // For new entries, let the repository generate a UUID (no existing file to orphan).
        val filename = if (isEdit) "${entry.id}.jpg" else null

        val result = receiptStorageRepository.uploadReceipt(pending.bytes, orgId, userId, filename)
        if (!result.error.isNullOrEmpty()) {
            _uiState.update { it.copy(isSaving = false, error = result.error) }
            return null
        }
        val url = result.data as String
        return entry.copy(
            tripTypeData = (entry.tripTypeData as TripTypeData.Fuel).copy(receiptImageUrl = url)
        )
    }

    fun carriedForwardStartOrNull(
        entry: MileageEntry,
        previousByDate: MileageEntry?,
    ): MileageEntry? {
        if (previousByDate == null) return null
        if (previousByDate.date.date == entry.date.date) return null
        if (previousByDate.tripType != TripType.START_END) return null
        if (entry.tripType == TripType.START_END && entry.mileageValue == previousByDate.mileageValue) return null
        val fiveMinutesBefore = entry.date
            .toInstant(TimeZone.currentSystemDefault())
            .minus(5.minutes)
            .toLocalDateTime(TimeZone.currentSystemDefault())
        val startDate = if (fiveMinutesBefore.date == entry.date.date) {
            fiveMinutesBefore
        } else {
            entry.date.date.atTime(0, 0)
        }
        return MileageEntry(
            id = "",
            date = startDate,
            employeeId = entry.employeeId,
            equipmentId = previousByDate.equipmentId,
            trailerId = previousByDate.trailerId,
            mileageValue = previousByDate.mileageValue,
            tripType = TripType.START_END,
            tripTypeData = TripTypeData.StartEnd(
                (previousByDate.tripTypeData as? TripTypeData.StartEnd)?.location ?: ""
            ),
        )
    }
}
